import { isViewable } from './documents-data';

import type { DocumentItem, DocumentType } from './documents-data';
import type { NotReadReason, ProcessingState } from './documents-api-types';

export const PENDING_REFETCH_MS = 5000;

export type ProcessingStatus = 'processing' | 'replacing' | 'failed' | 'not_read' | 'partly_read';

type ProcessingFields = {
  processingState: ProcessingState | null;
  replacing: boolean;
  deletedAt: string | null;
};

export const NOT_READ_TEXT: Record<NotReadReason, string> = {
  unsupported: 'Ari cannot read this type of file.',
  allowance: "This month's reading allowance is used. It is read next month or after an upgrade.",
  no_reader: 'Ari cannot read this yet.',
  images_only: 'This file contains only images, so there is no text for Ari to read.',
  too_large: 'Too large for Ari to read.',
  protected: 'This PDF is protected by a password. Replace it with a copy that has none.',
  empty: 'Ari found no text or speech in this file.',
};

const IMAGES_ONLY_PDF = 'This PDF contains only images, so there is no text for Ari to read.';

/**
 * No reader is one reason for a picture, a recording and, before CLOUD-1080, a PDF or Office file with only images.
 * The file type tells them apart, so older files get the clear message too.
 */
const NO_READER_TEXT: Partial<Record<DocumentType, string>> = {
  pdf: IMAGES_ONLY_PDF,
  document: NOT_READ_TEXT.images_only,
  presentation: NOT_READ_TEXT.images_only,
  image: 'Ari cannot read the text in images yet.',
  audio: 'Ari cannot listen to audio or video files yet.',
  video: 'Ari cannot listen to audio or video files yet.',
};

export function notReadText(item: Pick<DocumentItem, 'type' | 'notReadReason'>): string {
  switch (item.notReadReason) {
    case null:
      return 'Ari did not read this file.';
    case 'no_reader':
      return NO_READER_TEXT[item.type] ?? NOT_READ_TEXT.no_reader;
    case 'images_only':
      return item.type === 'pdf' ? IMAGES_ONLY_PDF : NOT_READ_TEXT.images_only;
    default:
      // A tab opened before a deploy can get a reason it does not know.
      return NOT_READ_TEXT[item.notReadReason] ?? 'Ari did not read this file.';
  }
}

// Reading these again gives the same answer. Only a different file helps.
// No reader and images only change only when OCR or transcription is turned on. CLOUD-1080.
const FINAL_REASONS = new Set<NotReadReason>(['unsupported', 'protected', 'empty', 'no_reader', 'images_only']);

/** The states the card's stroke and dot do not show. */
export type AttentionStatus = Extract<ProcessingStatus, 'failed' | 'not_read' | 'partly_read'>;

export const ATTENTION_LABEL: Record<AttentionStatus, string> = {
  failed: 'Failed',
  not_read: 'Not read by Ari',
  partly_read: 'Some pages not read',
};

/**
 * The state is the shown version's: the new one while a replacement is on its
 * way. A replacement that failed is failed, never pending.
 */
export function isPending(item: ProcessingFields): boolean {
  if (item.deletedAt) return false;
  return item.processingState === 'queued' || item.processingState === 'processing';
}

export function processingStatus(item: DocumentItem): ProcessingStatus | null {
  if (item.deletedAt) return null;
  if (isPending(item)) return item.replacing ? 'replacing' : 'processing';
  if (item.processingState === 'failed') return 'failed';
  if (item.processingState === 'not_read') return 'not_read';
  if (isRead(item) && item.imagesOnlyPages > 0) return 'partly_read';
  return null;
}

/**
 * A replacement waiting for the allowance leaves the previous version in use and read,
 * so "Not read by Ari" would say the wrong thing about the file on screen. CLOUD-1076.
 * It is the only replacement that waits. For every other reason documents_mark_not_read
 * lets the new version take over at once, so the file is no longer replacing.
 */
export function attentionLabel(item: DocumentItem): string | null {
  const status = attentionStatus(item);
  if (!status) return null;
  if (status === 'not_read' && item.replacing && item.notReadReason === 'allowance') return 'Newer version waits';
  return ATTENTION_LABEL[status];
}

export function attentionStatus(item: DocumentItem): AttentionStatus | null {
  const status = processingStatus(item);
  return status === 'failed' || status === 'not_read' || status === 'partly_read' ? status : null;
}

/** Ari has read the version on screen. */
export function isRead(item: ProcessingFields): boolean {
  return !item.replacing && item.processingState === 'ready';
}

export function statusDetail(item: DocumentItem): string {
  switch (processingStatus(item)) {
    case 'processing':
      return 'Ari is reading this file.';
    case 'replacing':
      return 'Ari is reading a newer version. You can use this version until then.';
    case 'failed':
      return item.replacing
        ? 'Ari could not read the new version. It still uses the previous one.'
        : 'Ari could not read this file.';
    case 'not_read': {
      if (item.replacing && item.notReadReason === 'allowance') {
        return "This month's reading allowance is used, so the newer version is read next month or after an upgrade. Until then the previous version is the one you open, download and get answers from.";
      }
      const reason = notReadText(item);
      return item.replacing ? `${reason} Until then it uses the previous version.` : reason;
    }
    case 'partly_read':
      return imagesOnlyText(item);
    default:
      return '';
  }
}

/** Without OCR, Ari reads no page that holds only images. CLOUD-1081. */
function imagesOnlyText(item: DocumentItem): string {
  const pages = item.imagesOnlyPages;
  const images = pages === 1 ? '1 page contains only images.' : `${pages} pages contain only images.`;
  if (!item.pageCount || item.pageCount < pages) return images;
  return `Ari read ${item.pageCount - pages} of ${item.pageCount} pages. ${images}`;
}

export function canRetry(item: DocumentItem): boolean {
  if (item.deletedAt) return false;
  if (item.processingState === 'failed') return true;
  return item.processingState === 'not_read' && !(item.notReadReason && FINAL_REASONS.has(item.notReadReason));
}

const PDF_PREVIEW_TYPES = new Set<DocumentType>(['document', 'presentation', 'spreadsheet']);

export type PreviewSource = 'original' | 'table' | 'pdf' | 'preparing' | 'none';

// A text file shows whole in a frame. A large one stops the browser tab.
const MAX_TEXT_PREVIEW_BYTES = 5 * 1024 * 1024;
// A zipped sheet is read whole in the browser. A CSV is read only from its start.
const MAX_ZIPPED_SHEET_BYTES = 20 * 1024 * 1024;

export type SheetFormat = 'csv' | 'xlsx' | 'ods';

const SHEET_FORMATS: Record<string, SheetFormat> = { csv: 'csv', xlsx: 'xlsx', xlsm: 'xlsx', ods: 'ods' };

/** The format Ari Cloud reads as a table. An xls file has none and keeps its PDF preview. CLOUD-1159. */
export function sheetFormat(item: Pick<DocumentItem, 'type' | 'name'>): SheetFormat | null {
  if (item.type !== 'spreadsheet') return null;
  return SHEET_FORMATS[item.name.split('.').pop()?.toLowerCase() ?? ''] ?? null;
}

/** A CSV, xlsx or ods file that is too large to read as a table in the browser. */
export function isLargeSheet(item: DocumentItem): boolean {
  const sheet = sheetFormat(item);
  return sheet !== null && sheet !== 'csv' && item.sizeBytes > MAX_ZIPPED_SHEET_BYTES;
}

export function previewSource(item: DocumentItem): PreviewSource {
  if (item.type === 'text' && item.sizeBytes > MAX_TEXT_PREVIEW_BYTES) return 'none';
  if (isViewable(item)) return 'original';
  const sheet = sheetFormat(item);
  if (sheet === 'csv' || (sheet && item.sizeBytes <= MAX_ZIPPED_SHEET_BYTES)) return 'table';
  // The worker makes no PDF of a large xlsx or ods file, so nothing is on the way. CLOUD-1159.
  if (sheet) return item.hasPreviewPdf ? 'pdf' : 'none';
  if (!PDF_PREVIEW_TYPES.has(item.type)) return 'none';
  if (item.hasPreviewPdf) return 'pdf';
  return isPending(item) ? 'preparing' : 'none';
}
